import { AuthContextType } from './auth-context.type';
import { createContext, ReactNode, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from 'src/hooks/use-auth';
import i18n from 'src/i18n';

// Create the authentication context with an initial undefined value.
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider component wraps the application and provides authentication context.
 * @param children - The components that will have access to the authentication context.
 */
export function AuthProvider({ children }: { children?: ReactNode }) {
  const auth = useAuth(); // Retrieve authentication state and methods from the custom hook.

  // Provide the authentication context to the component tree.
  return <AuthContext.Provider value={auth}>{children || <Outlet />}</AuthContext.Provider>;
}

/**
 * Custom hook to access the authentication context.
 * Throws an error if used outside of an AuthProvider.
 */
export function useAuthContext() {
  const context = useContext(AuthContext); // Access the authentication context.

  // Throw an error if the hook is used outside of an AuthProvider.
  if (!context) {
    throw new Error(i18n.t('ERRORS.CONTEXT.AUTH'));
  }

  return context; // Return the context value if it exists.
}
